import {
  Card,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Row
} from 'antd'
import { FormInstance } from 'antd/lib'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect } from 'react'

import { useTreatmentServiceGetPregnancyMonitoringEndpoint } from 'src/api/queries'
import {
  GetPatientPregnancyMonitoringResponse,
  GetPregnancyMonitoringResponse,
  InterruptedPregnancyReason,
  PregnancyActionStatus,
  PregnancyLocalization,
  PregnancyType
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { Select } from 'src/components'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { toDayjs } from 'src/utils'

import { useStyles } from '../styles'

type Props = {
  form: FormInstance
  data?: GetPatientPregnancyMonitoringResponse | GetPregnancyMonitoringResponse
  pregnancyPlanId?: string
  isOnDetailsModal?: boolean
  isEditing?: boolean
}

export const ConsultationPregnancyCard = ({
  data: pregnancyDetails,
  pregnancyPlanId,
  form,
  isOnDetailsModal = false,
  isEditing = false
}: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const pregnancyActionStatus = Form.useWatch('pregnancyActionStatus', form)

  const { data: pregnancyDetailsConsultation, isLoading } =
    useTreatmentServiceGetPregnancyMonitoringEndpoint(
      {
        id: String(pregnancyPlanId)
      },
      [String(pregnancyPlanId)],
      {
        enabled: !!pregnancyPlanId
      }
    )

  const localizationOptions = Object.values(PregnancyLocalization).map(
    (item) => ({
      value: item,
      label: t(`pregnancy.pregnant_localization_status.${item}`)
    })
  )

  const pregnancyDetailsData = pregnancyDetailsConsultation ?? pregnancyDetails

  const pregnancyTypeOptions = Object.values(PregnancyType).map((item) => ({
    value: item,
    label: t(`pregnancy.pregnancy_type_status.${item}`)
  }))

  const pregnancyActionStatusOptions = Object.values(PregnancyActionStatus).map(
    (item) => ({
      value: item,
      label: t(`pregnancy.pregnancy_actions_statuses.${item}`)
    })
  )

  const interruptedPregnancyReasonOptions = Object.values(
    InterruptedPregnancyReason
  ).map((item) => ({
    value: item,
    label: t(`pregnancy.interrupted_pregnancy_reasons.${item}`)
  }))

  useEffect(() => {
    if (pregnancyDetailsData && form) {
      form.setFieldsValue({
        gestationalAgeInWeeks: pregnancyDetailsData?.gestationalAgeInWeeks,
        lastMenstrualPeriod: toDayjs(pregnancyDetailsData?.lastMenstrualPeriod),
        estimated_birth_date: toDayjs(
          pregnancyDetailsData?.expectedDateOfBirth
        ),
        pregnancyType: pregnancyDetailsData?.pregnancyType,
        pregnancyStatusChangedDate: toDayjs(
          pregnancyDetailsData?.pregnancyStatusChangedDate
        ),
        interruptedPregnancyReason:
          pregnancyDetailsData?.interruptedPregnancyReason,
        pregnancyStatusChangedComment:
          pregnancyDetailsData?.pregnancyStatusChangedComment,
        pregnancyMonitoringStatus:
          pregnancyDetailsData?.pregnancyMonitoringStatus,
        pregnancyLocalization: pregnancyDetailsData?.pregnancyLocalization,
        pregnancyActionStatus: pregnancyDetailsData?.pregnancyActionStatus
      })
    }
  }, [pregnancyDetailsData, form, isEditing])

  return (
    <Flex vertical gap={16}>
      <Card title={t('pregnancy.pregnancy')} loading={isLoading}>
        <Flex gap={24} vertical>
          <Flex vertical gap={24}>
            <Row gutter={[24, 24]}>
              <Col span={24}>
                <Form.Item
                  name={'gestationalAgeInWeeks'}
                  label={t('pregnancy.age')}
                  rules={requiredRules?.concat({
                    max: 40,
                    type: 'number'
                  })}
                >
                  <InputNumber
                    placeholder={t('pregnancy.age')}
                    maxLength={40}
                    className="full-width"
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="lastMenstrualPeriod"
                  label={t('pregnancy.last_gestation_date')}
                  rules={requiredRules}
                >
                  <DatePicker
                    className="full-width"
                    format={dayMonthYearBackSlash}
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name="estimated_birth_date"
                  label={t('pregnancy.estimated_birth_date')}
                  rules={requiredRules}
                >
                  <DatePicker
                    className="full-width"
                    format={dayMonthYearBackSlash}
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name={'pregnancyType'}
                  label={t('pregnancy.pregnancy_type')}
                  rules={requiredRules}
                >
                  <Select
                    options={pregnancyTypeOptions}
                    placeholder={t('pregnancy.pregnancy_type')}
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  name={'pregnancyLocalization'}
                  label={t('pregnancy.pregnancy_localization')}
                  rules={requiredRules}
                >
                  <Select
                    options={localizationOptions}
                    placeholder={t('pregnancy.pregnancy_localization')}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
          </Flex>

          {isOnDetailsModal && (
            <Card className="full-width">
              <Row gutter={[24, 24]}>
                {!!pregnancyDetailsData && (
                  <>
                    <Col span={12}>
                      <Form.Item
                        name={'pregnancyActionStatus'}
                        label={t('pregnancy.pregnancy_status')}
                        rules={requiredRules}
                      >
                        <Select
                          options={pregnancyActionStatusOptions}
                          placeholder={t('pregnancy.pregnancy_status')}
                          disabled={!isEditing}
                        />
                      </Form.Item>
                    </Col>

                    {pregnancyActionStatus ===
                      PregnancyActionStatus.INTERRUPTED_PREGNANCY && (
                      <Col span={12}>
                        <Form.Item
                          name={'interruptedPregnancyReason'}
                          label={t('pregnancy.interrupted_pregnancy_reason')}
                          rules={requiredRules}
                        >
                          <Select
                            options={interruptedPregnancyReasonOptions}
                            placeholder={t(
                              'pregnancy.interrupted_pregnancy_reason'
                            )}
                            disabled={!isEditing}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </>
                )}

                <Col span={12}>
                  <Form.Item
                    name={'pregnancyStatusChangedDate'}
                    label={t('common.date')}
                  >
                    <DatePicker
                      className="full-width"
                      format={dayMonthYearBackSlash}
                      disabled={!isEditing}
                    />
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name={'pregnancyStatusChangedComment'}
                    label={t('common.comment')}
                  >
                    <Input.TextArea
                      rows={5}
                      className={styles.textarea}
                      placeholder={t('common.leave_comment')}
                      disabled={!isEditing}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          )}
        </Flex>
      </Card>

      {!isOnDetailsModal && (
        <Card className="full-width">
          <Row gutter={[24, 24]}>
            {!!pregnancyDetailsData && (
              <>
                <Col span={12}>
                  <Form.Item
                    name={'pregnancyActionStatus'}
                    label={t('pregnancy.pregnancy_status')}
                    rules={requiredRules}
                  >
                    <Select
                      options={pregnancyActionStatusOptions}
                      placeholder={t('pregnancy.pregnancy_status')}
                      disabled={!isEditing}
                    />
                  </Form.Item>
                </Col>
                {pregnancyActionStatus ===
                  PregnancyActionStatus.INTERRUPTED_PREGNANCY && (
                  <Col span={12}>
                    <Form.Item
                      name={'interruptedPregnancyReason'}
                      label={t('pregnancy.interrupted_pregnancy_reason')}
                      rules={requiredRules}
                    >
                      <Select
                        options={interruptedPregnancyReasonOptions}
                        placeholder={t(
                          'pregnancy.interrupted_pregnancy_reason'
                        )}
                        disabled={!isEditing}
                      />
                    </Form.Item>
                  </Col>
                )}{' '}
              </>
            )}

            <Col span={24}>
              <Form.Item
                name={'pregnancyStatusChangedDate'}
                label={t('common.date')}
                normalize={(m: Dayjs) => {
                  return dayjs(m).format(yearMonthDaySlash)
                }}
                getValueProps={(value) => ({
                  value: toDayjs(value)
                })}
              >
                <DatePicker
                  className="full-width"
                  format={dayMonthYearBackSlash}
                  disabled={!isEditing}
                />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                name={'pregnancyStatusChangedComment'}
                label={t('common.comment')}
              >
                <Input.TextArea
                  rows={5}
                  className={styles.textarea}
                  placeholder={t('common.leave_comment')}
                  disabled={!isEditing}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      )}
    </Flex>
  )
}
